import {range} from '@benchling/js-utils';

import * as sequtil from 'molbio/SeqUtil';
import {Angle} from 'util/geometry/GeometryConstants';
import {getAngles} from 'util/geometry/GeometryUtil';

export const HOMOLOGY_STRIPE_WIDTH_PIXELS = 6;
export const HOMOLOGY_REGION_MIN_STRIPES = 4;

export function getBaseWidthStripeParams(
  sequenceLength: number,
  outerRadius: number,
  getAngle: (basePair: number) => number
): {
  useBaseWidthStripe: boolean;
  singleBaseAngleRadians: number;
  singleBaseArcLengthPixels: number;
} {
  // We also want stripes to be no narrower than the width of a base, which means they may be larger than
  // HOMOLOGY_STRIPE_WIDTH_PIXELS if we're zoomed in enough to see bases.
  const singleBaseAngles = getAngles(0, 1, sequenceLength, getAngle);
  // How many radians constitute the width of a base?
  const singleBaseAngleRadians = singleBaseAngles.endAngle - singleBaseAngles.startAngle;
  const singleBaseArcLengthPixels = singleBaseAngleRadians * outerRadius;

  // If the width of a base is greater than HOMOLOGY_STRIPE_WIDTH_PIXELS, make the stripes the width of a base
  // instead.
  const useBaseWidthStripe = singleBaseArcLengthPixels > HOMOLOGY_STRIPE_WIDTH_PIXELS;
  return {
    useBaseWidthStripe,
    singleBaseAngleRadians,
    singleBaseArcLengthPixels,
  };
}

/**
 * Data about where to draw assembly fragments on a circular sequence visualization.
 */
export interface CircularFragmentResultData {
  /** The angle, in radians, at which this stripe starts */
  pieceStartAngle: number;
  /** The angle, in radians, at which this stripe ends */
  pieceEndAngle: number;
  /** What color to fill the piece with */
  color: string;
}

/**
 * Calculates what colors to draw at what locations on a circular visualization for a homology fragment result
 * @param fragmentResult A FragmentResult Backbone entity representing where an assembly fragment lies on the
 *                       constructed sequence.
 * @param sequenceLength The length of the constructed sequence in base pairs.
 * @param outerRadius The outer radius of the circular view in pixels.
 * @param getAngle A function that takes in a base pair index and returns the angle in radians at which that
 *                 base pair should be drawn.
 */
export function getCircularHomologyStripeData(
  fragmentResult: {
    start: number;
    end: number;
    color: string;
    homologyRegionOtherColor: string;
  },
  sequenceLength: number,
  outerRadius: number,
  getAngle: (basePair: number) => Angle
): Array<CircularFragmentResultData> {
  const {start, end, color, homologyRegionOtherColor} = fragmentResult;
  const {startAngle, endAngle} = getAngles(start, end, sequenceLength, getAngle);

  // We want stripes where the outer arc length is HOMOLOGY_STRIPE_WIDTH_PIXELS. Find how many radians make
  // an arc of that size on the outer radius.
  const regionAngleRadians = endAngle - startAngle;
  const regionArcLengthPixels = regionAngleRadians * outerRadius;
  // What % of the region's outer arc length is a stripe?
  const stripeRatio = HOMOLOGY_STRIPE_WIDTH_PIXELS / regionArcLengthPixels;
  // How many radians are in a stripe?
  const stripeSizeRadians = stripeRatio * regionAngleRadians;

  const {useBaseWidthStripe, singleBaseAngleRadians, singleBaseArcLengthPixels} = getBaseWidthStripeParams(
    sequenceLength,
    outerRadius,
    getAngle
  );
  const arcLengthPixels = useBaseWidthStripe ? singleBaseArcLengthPixels : HOMOLOGY_STRIPE_WIDTH_PIXELS;
  const angleRadians = useBaseWidthStripe ? singleBaseAngleRadians : stripeSizeRadians;
  // How much to adjust the actual start angle to get to the angle where we need to start drawing
  let stripeAngleAdjustmentRadians: number;
  // How big of stripes to draw, in radians
  let angleSizes: Array<number>;
  if (regionArcLengthPixels < arcLengthPixels * HOMOLOGY_REGION_MIN_STRIPES) {
    // We want a minimum of HOMOLOGY_REGION_MIN_STRIPES stripes, even if the actual region wouldn't be that
    // large. Center the striped region over the center of the actual homology region.
    const adjustCCWPixels = -((arcLengthPixels * HOMOLOGY_REGION_MIN_STRIPES - regionArcLengthPixels) / 2);
    // What % of the total outer arc length to adjust?
    const adjustRatio = adjustCCWPixels / regionArcLengthPixels;
    stripeAngleAdjustmentRadians = adjustRatio * regionAngleRadians;
    angleSizes = Array(HOMOLOGY_REGION_MIN_STRIPES).fill(angleRadians);
  } else {
    // We want the starting stripe to be the next fragment's color, and the ending stripe to be the
    // current fragment's color. i.e. we want an even number of stripes.
    stripeAngleAdjustmentRadians = 0;
    if (useBaseWidthStripe) {
      // If we're using a base width stripe, we don't need to calculate the number of stripes based off the
      // angle size; and doing so will lead to floating point errors and associated angst.
      const numBases = sequtil.length(start, end, sequenceLength);
      angleSizes = Array(numBases).fill(angleRadians);
      if (numBases % 2 !== 0) {
        // Odd number of base sized stripes; we want to add a 1px stripe of the 'other' color on the end to
        // demarcate the end of the homology region, and shrink the last base sized stripe commensurately.
        const onePixelArcRadians = regionAngleRadians / regionArcLengthPixels;
        angleSizes[angleSizes.length - 1] -= onePixelArcRadians;
        angleSizes.push(onePixelArcRadians);
      }
    } else {
      // Calculate the number of full sized stripes that fit within the homology region.
      const fullSizedStripes = Math.floor(regionArcLengthPixels / arcLengthPixels);
      if (fullSizedStripes % 2 === 0) {
        // Even number of normal sized stripes; we want to stretch the starting and ending stripes to the
        // end of the homology region.
        const growByPixels = (regionArcLengthPixels % arcLengthPixels) / 2;
        const growByRatio = growByPixels / regionArcLengthPixels;
        const growByRadians = growByRatio * regionAngleRadians;
        angleSizes = Array(fullSizedStripes).fill(angleRadians);
        angleSizes[0] += growByRadians;
        angleSizes[fullSizedStripes - 1] += growByRadians;
      } else {
        // Odd number of normal sized stripes; we want to add a stripe to make an even number and shrink
        // the starting and ending stripe to fit the homology region.
        const shrinkByPixels = (arcLengthPixels - (regionArcLengthPixels % arcLengthPixels)) / 2;
        const shrinkByRatio = shrinkByPixels / regionArcLengthPixels;
        const shrinkByRadians = shrinkByRatio * regionAngleRadians;
        angleSizes = Array(fullSizedStripes + 1).fill(angleRadians);
        angleSizes[0] -= shrinkByRadians;
        angleSizes[fullSizedStripes] -= shrinkByRadians;
      }
    }
  }

  const homologyRegionStripes = [];
  let currentStripeAngle = stripeAngleAdjustmentRadians;
  for (const i of range(0, angleSizes.length - 1)) {
    // Start with the 'other' color.
    const stripeColor = i % 2 === 0 ? homologyRegionOtherColor : color;
    const stripeAngleStart = startAngle + currentStripeAngle;
    const stripeAngleEnd = stripeAngleStart + angleSizes[i];
    currentStripeAngle += angleSizes[i];
    homologyRegionStripes.push({
      pieceStartAngle: stripeAngleStart,
      pieceEndAngle: stripeAngleEnd,
      color: stripeColor,
    });
  }
  return homologyRegionStripes;
}

/**
 * Data about where to draw assembly fragments on a linear sequence visualization.
 */
interface LinearFragmentResultData {
  /** The x-translation, in pixels, from the left-hand side of the linear view, where the stripe starts */
  startPoint: number;
  /** The x-translation, in pixels, from the left-hand side of the linear view, where the stripe ends */
  endPoint: number;
  /** What color to fill the stripe with */
  color: string;
}

/**
 * Calculates what colors to draw at what locations on a linear visualization for a homology result fragment
 * @param fragmentResult An object with start and end points of the homology region in base pairs and the
 *                       colors to use to draw stripes
 * @param basePairToTranslateX A function that calculates where on an x-axis a given base pair index lies
 */
export function getLinearHomologyStripeData(
  fragmentResult: {
    start: number;
    end: number;
    color: string;
    homologyRegionOtherColor: string;
  },
  basePairToTranslateX: (basePair: number) => number
): Array<LinearFragmentResultData> {
  // For homology regions, we want 6px stripes of the two colors across the height of the axis and
  // the width of the region.
  let translate: number;
  let stripeWidths: Array<number>;
  const {start, end, color, homologyRegionOtherColor} = fragmentResult;
  const initialTranslateX = basePairToTranslateX(start);
  const width = basePairToTranslateX(end) - initialTranslateX;
  // We want a minimum of four stripes on homology regions, even if the actual region wouldn't be
  // that large.
  if (width < HOMOLOGY_STRIPE_WIDTH_PIXELS * HOMOLOGY_REGION_MIN_STRIPES) {
    translate = -((HOMOLOGY_STRIPE_WIDTH_PIXELS * HOMOLOGY_REGION_MIN_STRIPES - width) / 2);
    stripeWidths = Array(HOMOLOGY_REGION_MIN_STRIPES).fill(HOMOLOGY_STRIPE_WIDTH_PIXELS);
  } else {
    // We want the starting stripe to be the next fragment's color, and the ending stripe to be the
    // current fragment's color. i.e. we want an even number of stripes.
    translate = 0;
    // Calculate the number of full sized stripes that fit within the homology region.
    const fullSizedStripes = Math.floor(width / HOMOLOGY_STRIPE_WIDTH_PIXELS);
    if (fullSizedStripes % 2 === 0) {
      // Even number of full sized stripes; we want to stretch the starting and ending stripes to
      // the end of the homology region.
      const growBy = (width % HOMOLOGY_STRIPE_WIDTH_PIXELS) / 2;
      stripeWidths = Array(fullSizedStripes).fill(HOMOLOGY_STRIPE_WIDTH_PIXELS);
      stripeWidths[0] += growBy;
      stripeWidths[fullSizedStripes - 1] += growBy;
    } else {
      // Odd number of full sized stripes; we want to add a stripe to make an even number and shrink
      // the starting and ending stripe to fit the homology region.
      const shrinkBy = (HOMOLOGY_STRIPE_WIDTH_PIXELS - (width % HOMOLOGY_STRIPE_WIDTH_PIXELS)) / 2;
      stripeWidths = Array(fullSizedStripes + 1).fill(HOMOLOGY_STRIPE_WIDTH_PIXELS);
      stripeWidths[0] -= shrinkBy;
      stripeWidths[fullSizedStripes] -= shrinkBy;
    }
  }

  const fragmentResultData: Array<LinearFragmentResultData> = [];
  for (const i of range(0, stripeWidths.length - 1)) {
    fragmentResultData.push({
      startPoint: initialTranslateX + translate,
      endPoint: initialTranslateX + translate + stripeWidths[i],
      // Start with the 'other' color.
      color: i % 2 === 0 ? homologyRegionOtherColor : color,
    });
    translate += stripeWidths[i];
  }
  return fragmentResultData;
}
